import { authService } from '../services/auth.service';
import { toast } from '../components/layout/toast';
import { $ } from '../utils/dom';
import { getDisciplineGymBrandHeader } from '../components/layout/brand-logo';
import { getTyrisIcon } from '../components/ui/tyris-icon';

export interface View {
  render(): string | HTMLElement | Promise<string | HTMLElement>;
  afterRender?(): void | Promise<void>;
}

export interface Route {
  path: string;
  component: () => Promise<View> | View;
  requiresAuth?: boolean;
  allowedRoles?: ('ADMIN' | 'COACH' | 'STUDENT')[];
  guestOnly?: boolean;
}

class HashRouter {
  private routes: Route[] = [];
  private appContainer: HTMLElement | null = null;
  private currentLayout: 'fullscreen' | 'app' | null = null;
  private navigationId = 0;

  constructor() {
    window.addEventListener('hashchange', () => this.resolveRoute());
    window.addEventListener('load', () => this.resolveRoute());
    
    // Escuchar cambios de sesión para re-evaluar la ruta actual si cambian los privilegios
    window.addEventListener('auth_change', () => this.resolveRoute());
  }

  setContainer(container: HTMLElement): this {
    this.appContainer = container;
    return this;
  }

  register(routes: Route[]): this {
    this.routes = routes;
    return this;
  }

  getParams(): Record<string, string> {
    const hash = window.location.hash;
    const searchPart = hash.split('?')[1];
    const params: Record<string, string> = {};
    if (searchPart) {
      const searchParams = new URLSearchParams(searchPart);
      searchParams.forEach((value, key) => {
        params[key] = value;
      });
    }
    return params;
  }

  private cleanPath(hash: string): string {
    if (!hash) return '/';
    // Extraer hash sin query params y sin el símbolo #/
    const pathWithHash = hash.startsWith('#') ? hash.slice(1) : hash;
    const pathClean = pathWithHash.split('?')[0];
    return pathClean.startsWith('/') ? pathClean : `/${pathClean}`;
  }

  async resolveRoute() {
    if (!this.appContainer) return;

    const navId = ++this.navigationId;
    const currentHash = window.location.hash || '#/';
    const path = this.cleanPath(currentHash);

    // Buscar ruta coincidente (coincidencia exacta o ruta 404 por defecto)
    let route = this.routes.find(r => r.path === path);
    if (!route) {
      // Intentar buscar ruta comodín o redirigir a dashboard
      route = this.routes.find(r => r.path === '/dashboard') || this.routes[0];
    }

    const isAuthenticated = authService.isAuthenticated();

    // 1. Guardián: Solo Invitados (ej. Login)
    if (route.guestOnly && isAuthenticated) {
      window.location.hash = '#/dashboard';
      return;
    }

    // 2. Guardián: Requiere Autenticación
    if (route.requiresAuth && !isAuthenticated) {
      window.location.hash = '#/login';
      return;
    }

    // 3. Guardián: Verificación de Roles
    if (route.requiresAuth && route.allowedRoles && !authService.hasRole(route.allowedRoles)) {
      toast.error('Acceso denegado. No posees el rol requerido para visualizar este panel.');
      window.location.hash = '#/dashboard';
      return;
    }

    try {
      // Cargar el componente (puede ser importación asíncrona)
      const page = await route.component();
      const pageContent = await page.render();

      if (navId !== this.navigationId) return;

      // Determinar si la vista requiere el layout de la app protegida o pantalla completa
      const layoutRequired = route.requiresAuth ? 'app' : 'fullscreen';

      if (layoutRequired === 'fullscreen') {
        this.currentLayout = 'fullscreen';
        
        if (typeof pageContent === 'string') {
          this.appContainer.innerHTML = pageContent;
        } else {
          this.appContainer.innerHTML = '';
          this.appContainer.appendChild(pageContent);
        }
        
        if (navId !== this.navigationId) return;

        if (page.afterRender) {
          await page.afterRender();
        }
      } else {
        // Layout protegido: Dibujar barra lateral si no existe o si venimos de pantalla completa
        if (this.currentLayout !== 'app') {
          this.currentLayout = 'app';
          this.renderAppLayout();
        }

        if (navId !== this.navigationId) return;

        const viewArea = $('#app-view-area');
        if (viewArea) {
          if (typeof pageContent === 'string') {
            viewArea.innerHTML = pageContent;
          } else {
            viewArea.innerHTML = '';
            viewArea.appendChild(pageContent);
          }
        }

        if (navId !== this.navigationId) return;

        if (page.afterRender) {
          await page.afterRender();
        }
        this.highlightActiveSidebarLink(path);
      }
    } catch (error) {
      console.error('Error al resolver la ruta:', error);
      toast.error('Ha ocurrido un error al cargar la vista.');
    }
  }

  private renderAppLayout() {
    if (!this.appContainer) return;

    const user = authService.getUser();
    const name = user ? `${user.firstName} ${user.lastName}` : 'Usuario';
    const roleBadgeClass = user ? `badge-${user.role.toLowerCase()}` : '';
    const roleName = user?.role || 'STUDENT';

    const studentLinks = authService.hasRole(['STUDENT']) ? `
      <div class="sidebar-section-label">Mi Entrenamiento</div>
      <a href="#/workout" class="sidebar-link student-link">
        ${getTyrisIcon('comenzar-entrenamiento', { size: 18 })}
        <span>Comenzar Entrenamiento</span>
      </a>
      <a href="#/daily-routine" class="sidebar-link student-link">
        ${getTyrisIcon('rutina-del-dia', { size: 18 })}
        <span>Mi Rutina Activa</span>
      </a>
      <a href="#/workout-history" class="sidebar-link student-link">
        ${getTyrisIcon('mis-rutinas', { size: 18 })}
        <span>Historial Entrenamientos</span>
      </a>
      <a href="#/routines" class="sidebar-link student-link">
        ${getTyrisIcon('mis-rutinas', { size: 18 })}
        <span>Historial de Rutinas</span>
      </a>
      <a href="#/rankings" class="sidebar-link student-link">
        ${getTyrisIcon('mis-records', { size: 18 })}
        <span>Mis Récords (PRs)</span>
      </a>
      <a href="#/measures" class="sidebar-link student-link">
        ${getTyrisIcon('medidas', { size: 18 })}
        <span>Medidas Corporales</span>
      </a>
      <a href="#/attendance" class="sidebar-link student-link">
        ${getTyrisIcon('asistencia', { size: 18 })}
        <span>Mi Asistencia</span>
      </a>
      <a href="#/payments" class="sidebar-link student-link">
        ${getTyrisIcon('pagos', { size: 18 })}
        <span>Membresía & Pagos</span>
      </a>
      <a href="#/profile" class="sidebar-link student-link">
        ${getTyrisIcon('perfil', { size: 18 })}
        <span>Mi Perfil</span>
      </a>
    ` : '';

    const adminLinks = authService.hasRole(['ADMIN']) ? `
      <div class="sidebar-section-label">Gestión & Control</div>
      <a href="#/dashboard" class="sidebar-link admin-link" data-nav="dashboard">
        ${getTyrisIcon('panel-principal', { size: 18 })}
        <span>Dashboard Admin</span>
      </a>
      <a href="#/admin/students" class="sidebar-link admin-link" data-nav="admin-students">
        ${getTyrisIcon('socios', { size: 18 })}
        <span>Usuarios & Socios</span>
      </a>
      <a href="#/admin/payments" class="sidebar-link admin-link" data-nav="admin-payments">
        ${getTyrisIcon('pagos', { size: 18 })}
        <span>Pagos & Membresías</span>
      </a>
      <a href="#/admin/exercises" class="sidebar-link admin-link" data-nav="admin-exercises">
        ${getTyrisIcon('mis-rutinas', { size: 18 })}
        <span>Banco Ejercicios</span>
      </a>
      <a href="#/admin/machines" class="sidebar-link admin-link" data-nav="admin-machines">
        ${getTyrisIcon('máquinas', { size: 18 })}
        <span>Inventario Máquinas</span>
      </a>
      <a href="#/admin/routines" class="sidebar-link admin-link" data-nav="admin-routines">
        ${getTyrisIcon('rutina-del-dia', { size: 18 })}
        <span>Rutinas Asignadas</span>
      </a>
      <a href="#/admin/routine-templates" class="sidebar-link admin-link" data-nav="admin-templates">
        ${getTyrisIcon('mis-rutinas', { size: 18 })}
        <span>Plantillas Rutina</span>
      </a>
      <a href="#/admin/measures" class="sidebar-link admin-link" data-nav="admin-measures">
        ${getTyrisIcon('medidas', { size: 18 })}
        <span>Medidas Alumnos</span>
      </a>
      <a href="#/admin/attendance" class="sidebar-link admin-link" data-nav="admin-attendance">
        ${getTyrisIcon('asistencia', { size: 18 })}
        <span>Registro Asistencia</span>
      </a>
      <a href="#/admin/workout-history" class="sidebar-link admin-link" data-nav="admin-workout-history" data-testid="admin-global-workout-history-link">
        ${getTyrisIcon('en-curso', { size: 18 })}
        <span>Historial Global de Entrenamientos</span>
      </a>
      <a href="#/admin/rankings" class="sidebar-link admin-link" data-nav="admin-rankings">
        ${getTyrisIcon('rankings', { size: 18 })}
        <span>Rankings & Liderazgo</span>
      </a>
    ` : '';

    const coachLinks = authService.hasRole(['COACH']) && !authService.hasRole(['ADMIN']) ? `
      <div class="sidebar-section-label">Panel Entrenador</div>
      <a href="#/dashboard" class="sidebar-link admin-link" data-nav="dashboard">
        ${getTyrisIcon('panel-principal', { size: 18 })}
        <span>Panel Principal</span>
      </a>
      <a href="#/admin/students" class="sidebar-link admin-link" data-nav="admin-students">
        ${getTyrisIcon('socios', { size: 18 })}
        <span>Mis Alumnos</span>
      </a>
      <a href="#/admin/routines" class="sidebar-link admin-link" data-nav="admin-routines">
        ${getTyrisIcon('rutina-del-dia', { size: 18 })}
        <span>Rutinas Asignadas</span>
      </a>
      <a href="#/admin/routine-templates" class="sidebar-link admin-link" data-nav="admin-templates">
        ${getTyrisIcon('mis-rutinas', { size: 18 })}
        <span>Templates</span>
      </a>
      <a href="#/admin/measures" class="sidebar-link admin-link" data-nav="admin-measures">
        ${getTyrisIcon('medidas', { size: 18 })}
        <span>Medidas</span>
      </a>
      <a href="#/admin/attendance" class="sidebar-link admin-link" data-nav="admin-attendance">
        ${getTyrisIcon('asistencia', { size: 18 })}
        <span>Asistencia</span>
      </a>
      <a href="#/admin/rankings" class="sidebar-link admin-link" data-nav="admin-rankings">
        ${getTyrisIcon('rankings', { size: 18 })}
        <span>Rankings</span>
      </a>
      <a href="#/admin/exercises" class="sidebar-link admin-link" data-nav="admin-exercises">
        ${getTyrisIcon('mis-rutinas', { size: 18 })}
        <span>Ejercicios</span>
      </a>
    ` : '';

    this.appContainer.innerHTML = `
      <!-- Mobile Top Bar -->
      <div class="mobile-topbar" id="mobile-topbar">
        <div class="mobile-topbar-brand">
          ${getDisciplineGymBrandHeader({ size: 18, showSubtitle: false, isCompact: true })}
        </div>
        <button class="mobile-menu-btn" id="btn-mobile-menu" aria-label="Abrir menú">
          ${getTyrisIcon('menú', { size: 20 })}
        </button>
      </div>

      <!-- Overlay -->
      <div class="sidebar-overlay" id="sidebar-overlay"></div>

      <div class="app-container">
        <!-- Sidebar -->
        <aside class="app-sidebar" id="app-sidebar">
          <div class="sidebar-header">
            ${getDisciplineGymBrandHeader({ size: 24, showSubtitle: true, className: 'discipline-brand-wrapper sidebar-brand' })}
          </div>
          
          <nav class="sidebar-nav">
            ${authService.hasRole(['STUDENT']) && !authService.hasRole(['ADMIN', 'COACH']) ? `
              <a href="#/dashboard" class="sidebar-link student-link">
                ${getTyrisIcon('panel-principal', { size: 18 })}
                <span>Panel Principal</span>
              </a>
            ` : ''}
            ${adminLinks}
            ${coachLinks}
            ${studentLinks}
          </nav>

          <div class="sidebar-footer">
            <div style="margin-bottom:0.75rem;">
              <div class="sidebar-user-name">${name}</div>
              <div style="margin-top:0.25rem;">
                <span class="badge ${roleBadgeClass}">${roleName}</span>
              </div>
            </div>
            <button id="btn-app-logout" class="btn btn-outline" style="padding:0.45rem;font-size:0.82rem;width:100%;border-color:rgba(229,72,77,0.3);color:var(--color-error);">
              ${getTyrisIcon('darse-de-baja', { size: 16 })}
              <span>Cerrar Sesión</span>
            </button>
          </div>
        </aside>

        <!-- Main Content -->
        <main id="app-view-area" class="main-content"></main>
      </div>
    `;

    // Bind logout
    const logoutBtn = $('#btn-app-logout');
    if (logoutBtn) {
      logoutBtn.addEventListener('click', () => {
        authService.logout();
        toast.info('Sesión finalizada correctamente.');
      });
    }

    // Bind mobile hamburger
    const menuBtn = $('#btn-mobile-menu');
    const sidebar = $('#app-sidebar');
    const overlay = $('#sidebar-overlay');

    const closeSidebar = () => {
      sidebar?.classList.remove('open');
      overlay?.classList.remove('visible');
    };

    if (menuBtn && sidebar && overlay) {
      menuBtn.addEventListener('click', () => {
        sidebar.classList.toggle('open');
        overlay.classList.toggle('visible');
      });

      overlay.addEventListener('click', closeSidebar);

      sidebar.querySelectorAll('.sidebar-link').forEach(link => {
        link.addEventListener('click', closeSidebar);
      });
    }
  }

  private highlightActiveSidebarLink(path: string) {
    const sidebar = document.getElementById('app-sidebar');
    if (!sidebar) return;
    sidebar.querySelectorAll('.sidebar-link').forEach(link => {
      const href = link.getAttribute('href');
      if (href) {
        const cleanHref = href.split('?')[0];
        const cleanPath = ('#' + path).split('?')[0];
        if (cleanHref === cleanPath) {
          link.classList.add('active');
        } else {
          link.classList.remove('active');
        }
      }
    });
  }
}

export const router = new HashRouter();
